<template>
    <v-container class="py-12">
        <v-row>
            <v-col :cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Registrar manutenção preventiva</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›"
                            :items="dataset.breadcrumbItems"></v-breadcrumbs>
                    </div>
                    <div class="button-group">
                        <v-btn id="btnSave" type="button" elevation="0" color="primary" dark @click="save"
                            :loading="dataset.loading">Salvar</v-btn>
                        <v-btn id="btnBack" type="button" elevation="0" color="primary" outlined
                            to="/manutencoes">Voltar</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <!-- Contexto do veículo -->
        <v-row v-if="vehicleContext">
            <v-col cols="12">
                <v-card class="card-form-outlined">
                    <v-card-text>
                        <div class="d-flex align-center">
                            <v-icon color="primary" class="mr-3">mdi-car</v-icon>
                            <div>
                                <div class="body-2 font-weight-medium">{{ vehicleContext.plate }} - {{ vehicleContext.brand }} {{ vehicleContext.model }}</div>
                                <div class="caption grey--text">{{ vehicleContext.schedulingName }} | Intervalo: {{ vehicleContext.intervalLabel }}</div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-form ref="form" v-model="dataForm.validForm">
            <v-row>
                <v-col cols="12">
                    <v-card class="card-form-outlined">
                        <v-card-title class="card-group-title">Informações gerais</v-card-title>
                        <v-card-text>
                            <v-alert v-if="requiresOdometer" type="info" dense text class="mb-3">
                                Informe o hodômetro atual para confirmar esta execução.
                            </v-alert>
                            <v-alert v-else-if="requiresHourMeter" type="info" dense text class="mb-3">
                                Informe o horímetro atual para confirmar esta execução.
                            </v-alert>
                            <v-alert v-else-if="requiresTimeBased" type="info" dense text class="mb-3">
                                Informe ao menos a data de início ou fim da manutenção.
                            </v-alert>

                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-select dense outlined :items="dataset.suppliers"
                                        :item-text="formatSupplierLabel" item-value="id" label="Fornecedor"
                                        v-model="dataset.data.supplier_id" :loading="dataset.resourcesLoading"
                                        clearable hide-details="auto"></v-select>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select dense outlined :items="dataset.statuses" item-text="label"
                                        item-value="value" label="Status"
                                        v-model="dataset.data.maintenance_status"
                                        hide-details="auto"></v-select>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6" v-if="requiresOdometer">
                                    <v-text-field dense outlined type="number" label="Hodômetro (KM)"
                                        v-model="dataset.data.odometer"
                                        :hint="vehicleContext && vehicleContext.km ? `KM atual registrado: ${vehicleContext.km.toLocaleString('pt-BR')}` : ''"
                                        persistent-hint hide-details="auto"></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6" v-if="requiresHourMeter">
                                    <v-text-field dense outlined type="number" label="Horímetro"
                                        v-model="dataset.data.hour_meter" hide-details="auto"></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field dense outlined label="Data início" type="datetime-local"
                                        v-model="dataset.data.started_at" hide-details="auto"></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field dense outlined label="Data fim" type="datetime-local"
                                        v-model="dataset.data.finished_at" hide-details="auto"></v-text-field>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card class="card-form-outlined">
                        <v-card-title class="card-group-title">Custos</v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field dense outlined label="Custo mão de obra" prefix="R$"
                                        type="number" v-model="dataset.data.labor_cost" min="0" step="0.01"
                                        hide-details="auto"></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field dense outlined label="Custo externo" prefix="R$"
                                        type="number" v-model="dataset.data.external_cost" min="0" step="0.01"
                                        hide-details="auto"></v-text-field>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card class="card-form-outlined">
                        <v-card-title class="card-group-title">Descrição do serviço</v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12">
                                    <v-textarea dense outlined label="Problema reportado"
                                        v-model="dataset.data.reported_issue" rows="2" auto-grow
                                        hide-details="auto"></v-textarea>
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea dense outlined label="Diagnóstico"
                                        v-model="dataset.data.diagnosis" rows="2" auto-grow
                                        hide-details="auto"></v-textarea>
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea dense outlined label="Serviço executado"
                                        v-model="dataset.data.work_performed" rows="2" auto-grow
                                        hide-details="auto"></v-textarea>
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea dense outlined label="Observações"
                                        v-model="dataset.data.notes" rows="2" auto-grow
                                        hide-details="auto"></v-textarea>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card class="card-form-outlined">
                        <v-card-title class="card-group-title d-flex justify-space-between align-center">
                            <span>Peças utilizadas</span>
                            <v-btn small elevation="0" color="primary" @click="addPartRow">
                                <v-icon small left>mdi-plus</v-icon>
                                Adicionar peça
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <v-simple-table dense v-if="dataset.data.parts && dataset.data.parts.length">
                                <thead>
                                    <tr>
                                        <th class="text-left" style="min-width: 200px">Peça</th>
                                        <th class="text-left" style="width: 100px">Qtd.</th>
                                        <th class="text-left" style="width: 140px">Valor unit.</th>
                                        <th class="text-left" style="width: 120px">Subtotal</th>
                                        <th style="width: 48px"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(part, index) in dataset.data.parts" :key="part.uid">
                                        <td class="py-2">
                                            <v-select dense outlined hide-details :items="dataset.partStocks"
                                                :item-text="formatPartOption" item-value="id"
                                                v-model="part.part_stock_id"
                                                @change="onPartSelection(part, $event)"
                                                placeholder="Selecione..."></v-select>
                                        </td>
                                        <td class="py-2">
                                            <v-text-field dense outlined hide-details type="number" min="1"
                                                v-model="part.quantity"></v-text-field>
                                        </td>
                                        <td class="py-2">
                                            <v-text-field dense outlined hide-details type="number" prefix="R$"
                                                min="0" step="0.01" v-model="part.unit_price"></v-text-field>
                                        </td>
                                        <td class="py-2">
                                            <span class="body-2 font-weight-medium">{{ formatCurrency(calculatePartTotal(part)) }}</span>
                                        </td>
                                        <td class="py-2">
                                            <v-btn icon x-small color="error" @click="removePartRow(index)">
                                                <v-icon small>mdi-delete-outline</v-icon>
                                            </v-btn>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                            <v-alert v-else type="info" dense text class="mt-2">Nenhuma peça adicionada.</v-alert>

                            <v-sheet outlined rounded class="pa-3 mt-4">
                                <v-row dense>
                                    <v-col cols="4" class="text-center">
                                        <div class="caption grey--text">Peças</div>
                                        <div class="body-2 font-weight-medium">{{ formatCurrency(partsTotal) }}</div>
                                    </v-col>
                                    <v-col cols="4" class="text-center">
                                        <div class="caption grey--text">Mão de obra + Externo</div>
                                        <div class="body-2 font-weight-medium">{{ formatCurrency(ensureNumeric(dataset.data.labor_cost) + ensureNumeric(dataset.data.external_cost)) }}</div>
                                    </v-col>
                                    <v-col cols="4" class="text-center">
                                        <div class="caption grey--text">Total estimado</div>
                                        <div class="text-subtitle-1 font-weight-bold primary--text">{{ formatCurrency(totalEstimate) }}</div>
                                    </v-col>
                                </v-row>
                            </v-sheet>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<script>
import axios from 'axios'

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
})

export default {
    name: 'PreventiveMaintenanceDetails',

    data: () => ({
        dataset: {
            data: {
                maintenance_scheduling_vehicle_id: null,
                supplier_id: null,
                maintenance_status: 'open',
                odometer: null,
                hour_meter: null,
                started_at: null,
                finished_at: null,
                reported_issue: '',
                diagnosis: '',
                work_performed: '',
                notes: '',
                labor_cost: null,
                external_cost: null,
                parts: [],
            },
            breadcrumbItems: [],
            loading: false,
            resourcesLoading: false,
            suppliers: [],
            partStocks: [],
            statuses: [],
        },
        dataForm: {
            validForm: true,
        },
        vehicleContext: null,
    }),

    computed: {
        requiresOdometer() {
            return this.vehicleContext && (this.vehicleContext.triggerType || '') === 'odometer'
        },
        requiresHourMeter() {
            return this.vehicleContext && (this.vehicleContext.triggerType || '') === 'hour_meter'
        },
        requiresTimeBased() {
            return this.vehicleContext && (this.vehicleContext.triggerType || '') === 'time'
        },
        partsTotal() {
            return (this.dataset.data.parts || []).reduce((total, part) => total + this.calculatePartTotal(part), 0)
        },
        totalEstimate() {
            return this.partsTotal + this.ensureNumeric(this.dataset.data.labor_cost) + this.ensureNumeric(this.dataset.data.external_cost)
        },
    },

    created() {
        this.loadData()
    },

    methods: {
        async loadData() {
            this.listBreadCrumbItems()

            const msvId = this.$route.query.msv_id
            if (msvId) {
                this.dataset.data.maintenance_scheduling_vehicle_id = Number(msvId)
            }

            await this.loadResources()
            await this.loadVehicleContext()
        },

        goBack() {
            this.$router.replace('/manutencoes')
        },

        listBreadCrumbItems() {
            this.dataset.breadcrumbItems = [
                { text: 'Homepage', disabled: false, to: '/home', exact: true },
                { text: 'Manutenções', disabled: false, to: '/manutencoes', exact: true },
                { text: 'Registrar manutenção', disabled: true, exact: true },
            ]
        },

        ensureNumeric(value) {
            const parsed = Number(value)
            return Number.isFinite(parsed) ? parsed : 0
        },

        formatCurrency(value) {
            return currencyFormatter.format(Number(value) || 0)
        },

        calculatePartTotal(part) {
            return this.ensureNumeric(part.quantity) * this.ensureNumeric(part.unit_price)
        },

        formatSupplierLabel(supplier) {
            if (!supplier) return ''
            const doc = supplier.document ? ` - ${supplier.document}` : ''
            return `${supplier.name}${doc}`
        },

        formatPartOption(part) {
            if (!part) return ''
            const code = part.sku ? ` (${part.sku})` : ''
            const balance = typeof part.current_quantity === 'number' ? ` | Estoque: ${part.current_quantity}` : ''
            return `${part.name}${code}${balance}`
        },

        onPartSelection(part, partId) {
            if (!partId || part.unit_price) return
            const selected = this.dataset.partStocks.find((stock) => stock.id === partId)
            if (selected) part.unit_price = selected.average_cost || 0
        },

        addPartRow() {
            if (!this.dataset.data.parts) this.dataset.data.parts = []
            this.dataset.data.parts.push({
                uid: `${Date.now()}-${Math.random()}`,
                part_stock_id: null,
                quantity: 1,
                unit_price: null,
                notes: '',
            })
        },

        removePartRow(index) {
            this.dataset.data.parts.splice(index, 1)
        },

        async loadResources() {
            this.dataset.resourcesLoading = true
            try {
                const { data } = await axios.get('api/v2/maintenance/preventive/resources')
                const content = data?.content || {}
                this.dataset.suppliers = content.suppliers || []
                this.dataset.partStocks = content.partStocks || []
                this.dataset.statuses = content.statuses || []
            } catch (error) {
                this.$dialog.notify.error('Erro ao carregar recursos.', { position: 'top-right', timeout: 5000 })
            } finally {
                this.dataset.resourcesLoading = false
            }
        },

        async loadVehicleContext() {
            const msvId = this.$route.query.msv_id
            if (!msvId) return

            try {
                const { data } = await axios.get('api/v2/maintenance/preventive', {
                    params: { page: 1, limit: 999 },
                })
                const vehicles = (data?.content?.data || [])
                const found = vehicles.find((v) => v.maintenanceSchedulingVehicleId == msvId)

                if (found) {
                    this.vehicleContext = {
                        plate: found.plate || 'N/A',
                        brand: found.brand || '',
                        model: found.model || '',
                        schedulingName: found.schedulingName || '',
                        intervalLabel: found.maintenanceIntervalLabel || (found.maintenanceKm ? `${found.maintenanceKm} KM` : 'Sem intervalo'),
                        triggerType: (found.triggerType || 'odometer').toLowerCase(),
                        km: found.km != null ? Number(found.km) : null,
                        defaultSupplierId: found.defaultSupplierId,
                    }

                    if (this.vehicleContext.defaultSupplierId) {
                        this.dataset.data.supplier_id = this.vehicleContext.defaultSupplierId
                    }

                    if (this.vehicleContext.triggerType === 'odometer' && this.vehicleContext.km) {
                        this.dataset.data.odometer = this.vehicleContext.km
                    }
                }
            } catch (error) {
                console.error('Erro ao carregar contexto do veículo:', error)
            }
        },

        formatDateTimeForPayload(value) {
            if (!value) return null
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return null
            const pad = (n) => String(n).padStart(2, '0')
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
        },

        validateForm() {
            if (!this.dataset.data.maintenance_scheduling_vehicle_id) return 'Programação do veículo não identificada.'
            if (this.requiresOdometer && !this.dataset.data.odometer) return 'Informe o hodômetro atual.'
            if (this.requiresHourMeter && !this.dataset.data.hour_meter) return 'Informe o horímetro atual.'
            if (this.requiresTimeBased && !this.dataset.data.started_at && !this.dataset.data.finished_at) return 'Informe ao menos a data de início ou fim.'
            return null
        },

        async save() {
            const validationMessage = this.validateForm()
            if (validationMessage) {
                this.$dialog.notify.error(validationMessage, { position: 'top-right', timeout: 5000 })
                return
            }

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja registrar a manutenção preventiva?',
                title: 'Registrar manutenção',
                actions: { false: 'Cancelar', true: 'Salvar' },
            })

            if (!proceedAction) return

            this.dataset.loading = true

            try {
                const normalizeNumber = (v) => {
                    if (v === undefined || v === null || v === '') return null
                    const parsed = Number(v)
                    return Number.isFinite(parsed) ? parsed : null
                }

                const payload = {
                    maintenance_scheduling_vehicle_id: this.dataset.data.maintenance_scheduling_vehicle_id,
                    supplier_id: this.dataset.data.supplier_id || null,
                    maintenance_status: this.dataset.data.maintenance_status || 'open',
                    odometer: normalizeNumber(this.dataset.data.odometer),
                    hour_meter: normalizeNumber(this.dataset.data.hour_meter),
                    started_at: this.formatDateTimeForPayload(this.dataset.data.started_at),
                    finished_at: this.formatDateTimeForPayload(this.dataset.data.finished_at),
                    reported_issue: this.dataset.data.reported_issue || null,
                    diagnosis: this.dataset.data.diagnosis || null,
                    work_performed: this.dataset.data.work_performed || null,
                    notes: this.dataset.data.notes || null,
                    labor_cost: normalizeNumber(this.dataset.data.labor_cost),
                    external_cost: normalizeNumber(this.dataset.data.external_cost),
                    parts: (this.dataset.data.parts || [])
                        .filter((part) => part.part_stock_id && this.ensureNumeric(part.quantity) > 0)
                        .map((part) => ({
                            part_stock_id: part.part_stock_id,
                            quantity: this.ensureNumeric(part.quantity),
                            unit_price: normalizeNumber(part.unit_price),
                            notes: part.notes || null,
                        })),
                }

                await axios.post('api/v2/maintenance/preventive', payload)

                this.$dialog.notify.info('Manutenção registrada com sucesso!', { position: 'top-right', timeout: 2000 })

                this.goBack()
            } catch (error) {
                this.$dialog.notify.error(this.$codes ? this.$codes(error) : 'Erro ao registrar manutenção.', {
                    position: 'top-right', timeout: 5000,
                })
            } finally {
                this.dataset.loading = false
            }
        },
    },
}
</script>
